import { type FetchOptions } from 'ofetch';
import { getCsrfToken } from '~/composables/useCsrfToken';
import {
  getFetchNetworkErrorMessage,
  handleSessionExpiredFromHeaders,
  isLoginPath,
} from '~/composables/useSessionAuth';
import { notifyApiError, notifyApiSuccess } from '~/composables/useApiToast';
import { appT } from '~/composables/useAppI18n';

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// @ts-ignore
interface ApiOptions<T> extends Omit<FetchOptions<T>, 'body' | 'query'> {
  method?: Method;
  data?: Record<string, any>; // JSON 요청 시 사용
  body?: BodyInit | null; // FormData, string, Blob, etc. 가능
  query?: Record<string, any>;
  skipLoading?: boolean;
}

/**
 * Nuxt 3용 multipart/form-data API 요청 컴포저블
 *
 * @param url 요청할 API 엔드포인트
 * @param options ofetch의 FetchOptions
 */
export const useApi_multipart = <T = any>(url: string, options: ApiOptions<T> = {}) => {
  const config = useRuntimeConfig();
  const loadingCount = useState('loadingCount', () => 0);

  // 환경별 URL 처리: 빌드환경에서는 /api 제거
  const processedUrl = config.public.isDev
    ? url // 개발환경: 그대로 사용 (프록시가 처리)
    : url.replace(/^\/api\//, '/'); // 빌드환경: /api/ 제거

  const defaults: FetchOptions = {
    baseURL: config.public.apiBase,
    credentials: 'include', // 🔥 JSESSIONID 쿠키를 보내야 세션 인식됨
    headers: {
      Accept: 'application/json',
      // 'Content-Type': 'multipart/form-data',
      'X-Requested-With': 'XMLHttpRequest',
      'X-Requested-From': 'nuxt',
    },
    // 요청 전처리
    onRequest({ options }) {
      if (process.client) {
        const headers = (options.headers ?? {}) as Record<string, string>;
        headers['X-Requested-With'] = headers['X-Requested-With'] || 'XMLHttpRequest';
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          const csrfHeaderName = document.querySelector('meta[name="_csrf_header"]')?.getAttribute('content') || 'X-XSRF-TOKEN';
          headers[csrfHeaderName] = csrfToken;
        }
        options.headers = headers;
      }

      const opts = options as any;
      if (opts.skipLoading !== true) {
        loadingCount.value++;
      }
    },

    // 응답 후처리
    onResponse({ response, options }) {
      const opts = options as any;
      if (opts.skipLoading !== true && loadingCount.value > 0) {
        loadingCount.value--;
      }

      // 세션 만료 체크
      if (handleSessionExpiredFromHeaders(response.headers)) {
        response._data = null;
        return;
      }

      // 레거시 코드의 'delete' 타입 응답 처리 예시
      if (response._data?.responseType === 'delete') {
        notifyApiSuccess(appT('common.api.deleteSuccess'));
      }
    },

    // 에러 발생 시 후처리
    onResponseError({ response, options }) {
      const opts = options as any;
      if (opts.skipLoading !== true && loadingCount.value > 0) {
        loadingCount.value--;
      }

      // 세션 만료 체크
      if (handleSessionExpiredFromHeaders(response?.headers)) {
        return;
      }

      const errorData = response?._data;
      let errorMessage = appT('common.api.processFailed');

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData?.msg) {
        errorMessage = errorData.msg;
      }

      if (errorData?.errorType === 'secure') {
        notifyApiError(appT('common.api.securityError'), errorMessage);
        return;
      }

      notifyApiError(appT('common.api.errorOccurred'), errorMessage);
    },

    // 요청 자체가 실패한 경우 (네트워크 에러, CORS 등)
    onRequestError({ error, options }) {
      const opts = options as any;
      if (opts.skipLoading !== true && loadingCount.value > 0) {
        loadingCount.value--;
      }
      if (isLoginPath()) {
        return;
      }
      notifyApiError(appT('common.api.networkError'), getFetchNetworkErrorMessage(error));
    },
  };

  const mergedOptions = {
    ...defaults,
    ...options,
    headers: {
      ...defaults.headers,
      ...options.headers,
    },
    body: options.body ?? options.data, // ← 이 라인 반드시 있어야 함
    query: options.query,
  };

  // $fetch는 ofetch의 래퍼이며, Nuxt에서 사용이 권장됩니다.
  // @ts-ignore
  return $fetch<T>(processedUrl, mergedOptions);
};
